import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Field, getIn } from 'formik';
import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { getGlobalSelectClass } from '@/utils'; // Global react-select styles
import { DefaultTooltip, KeenIcon } from '@/components';
import { collectAllOptions } from './selectAllOptions';

/** Helper function to get the id/value of an option */
const getIdOf = (opt, getOptionValue) => !opt ? undefined : (typeof getOptionValue === 'function' ? getOptionValue(opt) : opt.value);
/** Helper function to get the label of an option */
const getLabelOf = (opt, getOptionLabel) => !opt ? '' : (typeof getOptionLabel === 'function' ? getOptionLabel(opt) : opt.label);
/** Normalize ids for internal comparisons so "1" and 1 resolve to the same option */
const getComparableId = (value) => value === undefined || value === null ? undefined : String(value);

/** "Select All" pulls the whole match in pages of this size, and gives up after this many. */
const SELECT_ALL_PAGE_SIZE = 200;
const SELECT_ALL_MAX_PAGES = 25;
/** How many chips the control draws before it switches to a count. */
const MAX_VISIBLE_VALUES = 20;

/** ======================= Select All Control ======================= */
/**
 * This renders the "Select All / Deselect All" option at the top of a multi-select menu.
 * It works only for multi-select fields. The parent owns the logic, because on a
 * lazily loaded list selecting everything means fetching the pages that are not
 * in the menu yet.
 */
const SelectAllControl = ({ selectProps }) => {
  const { isMulti, _selectAll } = selectProps;
  if (!isMulti || !_selectAll) return null;

  const { allSelected, loading, notice, onToggle } = _selectAll;

  return (
    <div className="border-b border-gray-200">
      <div
        className={`px-3 py-2 text-sm font-semibold ${loading ? 'cursor-wait text-gray-500' : 'cursor-pointer hover:bg-gray-100'}`}
        onMouseDown={(e) => { e.preventDefault(); if (!loading) onToggle(); }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? 'Selecting all…' : allSelected ? 'Deselect All' : 'Select All'}
      </div>
      {notice && (
        <div className="px-3 pb-2 text-xs font-normal text-gray-500">{notice}</div>
      )}
    </div>
  );
};

/** ======================= Multi Value ======================= */
/**
 * Caps how many chips the control draws. Selecting a few hundred contacts is the
 * point of "Select All", and drawing a chip for every one of them makes the field
 * crawl, so past the cap we draw a count instead.
 */
const MultiValueWithLimit = (props) => {
  const { selectProps: { _maxVisibleValues } = {}, index, getValue } = props;
  const total = (getValue() || []).length;

  if (!_maxVisibleValues || total <= _maxVisibleValues) return <components.MultiValue {...props} />;
  if (index < _maxVisibleValues) return <components.MultiValue {...props} />;
  if (index > _maxVisibleValues) return null;

  return (
    <div className="px-2 py-0.5 m-0.5 text-xs text-gray-600 bg-gray-100 rounded">
      +{total - _maxVisibleValues} more selected
    </div>
  );
};

/** ======================= Menu List ======================= */
/**
 * Wraps the default react-select MenuList and injects the SelectAllControl.
 * If enableSelectAll is false, it renders the default MenuList only.
 */
const MenuListWithSelectAll = (props) => {
  const BaseMenuList = components.MenuList;
  const { selectProps: { enableSelectAll } = {} } = props;

  return enableSelectAll
    ? <BaseMenuList {...props}><SelectAllControl {...props} />{props.children}</BaseMenuList>
    : <BaseMenuList {...props}>{props.children}</BaseMenuList>;
};

/** ======================= Lazy Formik Select ======================= */
/**
 * Main component integrating react-select / creatable select with Formik
 * Features:
 * - Multi-select or single-select
 * - Creatable options
 * - Optional "Select All" for multi-select
 * - Optional dynamic filtering by key/value
 * - Fully controlled via Formik
 * - Async loading
 * - Virtualized list
 */
const LazyFormikSelect = ({
  name,
  placeholder,
  options = [],
  preloadedOption = null,
  onFocus,
  isMulti = false,
  isLoading,
  noOptionsMessage,
  transparent = false,
  returnFullObject = false,
  onOptionSelect,
  creatable = false,
  disabled,
  components: customComponents = {},
  customClassNames = {},
  closeMenuOnSelect = true,
  onChange,
  onInputChange,
  enableFilter = false,            // Enable key/value filtering UI
  multiFilter = false,            // Enable multiple filters at once
  enableFilterModeToggle = false, // Allow user to toggle single vs multi filter
  enableSelectAll = false,        // Enable "Select All / Deselect All"
  availableKeys = ['label'],     // Keys available for filtering
  filterKeys = [],              // Custom keys for filtering
  filterValueOptionsByKey = {}, // Optional: map of filterKey -> array of values or {label,value}
  serverFilterKeys = [],        // Optional: filter keys to pass to loadOptions
  getOptionValue,
  getOptionLabel,
  loadOptions,
  pageSize = 20,
  debounceMs = 300,
}) => {
  const keysToUse = filterKeys.length ? filterKeys : availableKeys;
  const filterKeyOptions = keysToUse.map(key => {
    const formattedKey =
      key === "label"
        ? "Contact Name"
        : key
          .split(/[_-]/)
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");

    return {
      value: key, // keep raw key
      label: `Filter by ${formattedKey}`,
    };
  });

  const formatFilterKeyLabel = (key) => {
    if (!key) return '';
    if (key === 'label') return 'Contact Name';
    return key
      .split(/[_-]/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };


  const [internalOptions, setInternalOptions] = useState(options);
  const [filterKey, setFilterKey] = useState(keysToUse[0] || 'label'); // currently selected filter key (single-filter mode)
  const [filterValue, setFilterValue] = useState('');                  // currently selected filter value (single-filter mode)
  const [filterModeMulti, setFilterModeMulti] = useState(Boolean(multiFilter));
  const [filterValues, setFilterValues] = useState(() => {
    const seed = {};
    keysToUse.forEach((key) => {
      seed[key] = '';
    });
    return seed;
  });
  const [menuPlacement, setMenuPlacement] = useState("auto");
  const selectRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const latestSearchRef = useRef('');
  const lastRequestSearchRef = useRef('');
  const isAsync = typeof loadOptions === 'function';
  const [asyncPage, setAsyncPage] = useState(1);
  const [asyncHasMore, setAsyncHasMore] = useState(true);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectAllNotice, setSelectAllNotice] = useState('');
  // What the last "Select All" covered, so the control can offer to undo exactly
  // that set instead of only the handful of options on screen.
  const [bulkSelection, setBulkSelection] = useState(null);
  const createdOptionsRef = useRef([]);
  const selectedOptionsRef = useRef(new Map());
  const lastServerFilterRef = useRef(null);

  const normalizeFilterValueOptions = useCallback((values) => {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const normalized = [];
    values.forEach((val) => {
      if (!val) return;
      const value = typeof val === 'object'
        ? String(val.value ?? val.label ?? '')
        : String(val);
      const label = typeof val === 'object'
        ? String(val.label ?? val.value ?? '')
        : String(val);
      if (!value || seen.has(value)) return;
      seen.add(value);
      normalized.push({ value, label });
    });
    return normalized;
  }, []);

  const effectiveMultiFilter = enableFilterModeToggle ? filterModeMulti : multiFilter;

  const getServerFilterParams = useCallback(() => {
    if (!enableFilter) return {};
    if (!Array.isArray(serverFilterKeys) || !serverFilterKeys.length) return {};

    if (effectiveMultiFilter) {
      return serverFilterKeys.reduce((acc, key) => {
        const value = filterValues?.[key];
        if (value) acc[key] = value;
        return acc;
      }, {});
    }

    if (!filterKey || !filterValue) return {};
    if (!serverFilterKeys.includes(filterKey)) return {};
    return { [filterKey]: filterValue };
  }, [enableFilter, filterKey, filterValue, serverFilterKeys, effectiveMultiFilter, filterValues]);

  const cacheOptions = useCallback((opts) => {
    if (!Array.isArray(opts)) return;
    opts.forEach(opt => {
      const id = getComparableId(getIdOf(opt, getOptionValue));
      if (id !== undefined && opt) selectedOptionsRef.current.set(id, opt);
    });
  }, [getOptionValue]);

  const preloadList = useMemo(() => {
    if (!preloadedOption) return [];
    return Array.isArray(preloadedOption) ? preloadedOption : [preloadedOption];
  }, [preloadedOption]);

  const shouldInjectPreloadedIntoOptions = !isAsync;

  const mergeOptions = useCallback((base, incoming) => {
    const merged = [];
    const seen = new Set();
    const append = (opt) => {
      const id = getComparableId(getIdOf(opt, getOptionValue));
      if (id === undefined || seen.has(id)) return;
      seen.add(id);
      merged.push(opt);
    };
    base.forEach(append);
    incoming.forEach(append);
    return merged;
  }, [getOptionValue]);

  const loadAsyncOptions = useCallback(async ({ search, page, replace, filters = {} }) => {
    if (!isAsync) return;
    const requestId = ++requestIdRef.current;
    lastRequestSearchRef.current = search;
    setAsyncLoading(true);
    try {
      const result = await loadOptions({ search, page, pageSize, ...filters });
      if (requestId !== requestIdRef.current) return;

      const nextOptions = result?.options || [];
      const hasMore = Boolean(result?.hasMore);

      cacheOptions(nextOptions);
      setAsyncHasMore(hasMore);
      setAsyncPage(page);
      setInternalOptions(prev => {
        const base = replace ? [] : prev;
        const merged = mergeOptions(base, nextOptions);
        return createdOptionsRef.current.length
          ? mergeOptions(merged, createdOptionsRef.current)
          : merged;
      });
    } finally {
      if (requestId === requestIdRef.current) setAsyncLoading(false);
    }
  }, [isAsync, loadOptions, mergeOptions, pageSize, cacheOptions]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!effectiveMultiFilter) return;
    setFilterValues((prev) => {
      const next = { ...prev };
      keysToUse.forEach((key) => {
        if (next[key] === undefined) next[key] = '';
      });
      return next;
    });
  }, [effectiveMultiFilter, JSON.stringify(keysToUse)]);

  useEffect(() => {
    if (!enableFilterModeToggle) return;
    setFilterModeMulti(Boolean(multiFilter));
  }, [enableFilterModeToggle, multiFilter]);

  useEffect(() => {
    if (!enableFilterModeToggle) return;
    if (filterModeMulti) {
      if (filterKey && filterValue) {
        setFilterValues((prev) => ({ ...prev, [filterKey]: filterValue }));
      }
    }
  }, [enableFilterModeToggle, filterModeMulti, filterKey, filterValue]);

  useLayoutEffect(() => {
    const el = selectRef.current?.controlRef;
    if (!el) return;
    const handleOpen = () => {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Minimum height threshold to decide flipping (~200px default menu height)
      setMenuPlacement(spaceBelow < 200 && spaceAbove > spaceBelow ? 'top' : 'bottom');
    };
    el.addEventListener('click', handleOpen);
    return () => el.removeEventListener('click', handleOpen);
  }, []);

  /** Merge incoming options and preloaded options only once */
  useEffect(() => {
    const combinedOptions = [...internalOptions];
    const incoming = Array.isArray(options) ? options : [];
    const preload = shouldInjectPreloadedIntoOptions ? preloadList : [];

    let changed = false;
    [...incoming, ...preload].forEach(opt => {
      const id = getComparableId(getIdOf(opt, getOptionValue));
      if (id === undefined) return;
      if (!combinedOptions.some(o => getComparableId(getIdOf(o, getOptionValue)) === id)) {
        combinedOptions.push(opt);
        changed = true;
      }
    });
    if (changed) setInternalOptions(combinedOptions);
    cacheOptions(incoming);
    cacheOptions(preload);
    if (!shouldInjectPreloadedIntoOptions) {
      cacheOptions(preloadList);
    }
  }, [JSON.stringify(options), JSON.stringify(preloadedOption), getOptionValue, preloadList, cacheOptions, shouldInjectPreloadedIntoOptions]);

  /** Compute unique values for the selected filter key */
  const availableValueOptionsByKey = useMemo(() => {
    const result = {};
    keysToUse.forEach((key) => {
      const external = filterValueOptionsByKey?.[key];
      if (external?.length) {
        result[key] = normalizeFilterValueOptions(external);
        return;
      }

      const seen = new Set();
      const values = [];
      internalOptions.forEach((opt) => {
        const raw = opt?.[key];
        if (!raw) return;
        const value = String(raw);
        if (seen.has(value)) return;
        seen.add(value);
        values.push({ value, label: value });
      });
      result[key] = values;
    });
    return result;
  }, [keysToUse, internalOptions, filterValueOptionsByKey, normalizeFilterValueOptions]);

  /** Does an option pass the filters the user has set? */
  const matchesActiveFilters = useCallback((opt) => {
    if (!enableFilter) return true;
    if (effectiveMultiFilter) {
      return Object.entries(filterValues || {})
        .filter(([, value]) => value)
        .every(([key, value]) => opt?.[key] === value);
    }
    if (!filterKey || !filterValue) return true;
    return opt?.[filterKey] === filterValue;
  }, [enableFilter, effectiveMultiFilter, filterValues, filterKey, filterValue]);

  return (
    <Field name={name}>
      {({ form }) => {
        const fieldValue = getIn(form.values, name);

        /** Compute filtered options based on filter key/value */
        const filteredOptions = useMemo(
          () => (enableFilter ? internalOptions.filter(matchesActiveFilters) : internalOptions),
          [internalOptions, enableFilter, matchesActiveFilters]
        );

        /** Compute currently selected option(s) */
        const selected = useMemo(() => {
          const optionById = new Map();
          internalOptions.forEach(opt => {
            const id = getComparableId(getIdOf(opt, getOptionValue));
            if (id !== undefined) optionById.set(id, opt);
          });
          preloadList.forEach(opt => {
            const id = getComparableId(getIdOf(opt, getOptionValue));
            if (id !== undefined && !optionById.has(id)) optionById.set(id, opt);
          });
          selectedOptionsRef.current.forEach((opt, id) => {
            if (!optionById.has(id)) optionById.set(id, opt);
          });
          if (returnFullObject) {
            const rawValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
            rawValues.forEach(val => {
              if (val && typeof val === 'object') {
                const id = getComparableId(getIdOf(val, getOptionValue));
                if (id !== undefined && !optionById.has(id)) optionById.set(id, val);
              }
            });
          }

          if (isMulti) {
            const valuesArray = Array.isArray(fieldValue) ? fieldValue : [];
            const valueIds = new Set(
              valuesArray
                .map(v => getComparableId(typeof v === 'object' ? getIdOf(v, getOptionValue) : v))
                .filter(id => id !== undefined)
            );
            const selectedOptions = [];
            valueIds.forEach(id => {
              const opt = optionById.get(id);
              if (opt) selectedOptions.push(opt);
            });
            return selectedOptions;
          }
          if (!fieldValue) return null;
          const targetId = getComparableId(typeof fieldValue === 'object' ? getIdOf(fieldValue, getOptionValue) : fieldValue);
          return optionById.get(targetId) || null;
        }, [fieldValue, isMulti, internalOptions, getOptionValue, returnFullObject, preloadList]);

        const SelectComponent = creatable ? CreatableSelect : Select;

        /** Handle creation of new options for creatable selects */
        const handleCreateOption = (inputValue) => {
          const newOption = { label: inputValue, value: inputValue };
          createdOptionsRef.current = mergeOptions(createdOptionsRef.current, [newOption]);
          cacheOptions([newOption]);
          setInternalOptions(prev => mergeOptions(prev, [newOption]));
          const newValue = isMulti
            ? [...(form.values[name] || []), returnFullObject ? newOption : getIdOf(newOption, getOptionValue)]
            : returnFullObject ? newOption : getIdOf(newOption, getOptionValue);
          form.setFieldValue(name, newValue);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          setInputValue('');
          latestSearchRef.current = '';
          if (onOptionSelect) onOptionSelect(newOption);
        };

        /** Handle selection changes (single or multi) */
        const handleChange = (selectedOption, actionMeta) => {
          if (selectedOption) {
            cacheOptions(Array.isArray(selectedOption) ? selectedOption : [selectedOption]);
          }
          const valueToStore = isMulti
            ? (selectedOption?.map(opt => returnFullObject ? opt : getIdOf(opt, getOptionValue)) || [])
            : returnFullObject ? selectedOption : selectedOption ? getIdOf(selectedOption, getOptionValue) : '';
          form.setFieldValue(name, valueToStore);

          if (actionMeta?.action === 'select-option' || actionMeta?.action === 'deselect-option' || actionMeta?.action === 'remove-value') {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            setInputValue('');
            latestSearchRef.current = '';
          }

          // Fire onOptionSelect only for single selections (skip bulk select all)
          if (onOptionSelect && selectedOption && !(actionMeta?.option?.isSelectAll)) {
            (Array.isArray(selectedOption) ? selectedOption : [selectedOption]).forEach(opt => onOptionSelect(opt));
          }
          if (onChange) onChange(selectedOption, actionMeta);
        };

        /** ---------------- Select All ---------------- */
        const toId = (opt) => getComparableId(getIdOf(opt, getOptionValue));
        const selectedList = isMulti && Array.isArray(selected) ? selected : [];
        const selectedIdSet = new Set(selectedList.map(toId));
        const displayedIds = filteredOptions.map(toId).filter(id => id !== undefined);

        // The search and filters the next "Select All" would run against. When they
        // change, whatever an earlier "Select All" covered no longer applies.
        const selectAllScope = JSON.stringify({
          search: inputValue || '',
          filters: isAsync ? getServerFilterParams() : {},
        });
        const bulkIds = bulkSelection?.scope === selectAllScope ? bulkSelection.ids : null;

        // "Deselect All" is only honest once everything in scope is selected. On an
        // async list with pages still unread, that is only true after a bulk select.
        const allSelected = bulkIds?.length
          ? bulkIds.every(id => selectedIdSet.has(id))
          : displayedIds.length > 0
          && !(isAsync && asyncHasMore)
          && displayedIds.every(id => selectedIdSet.has(id));

        const handleToggleSelectAll = async () => {
          if (selectAllLoading) return;

          if (allSelected) {
            const removeIds = new Set(bulkIds?.length ? bulkIds : displayedIds);
            setBulkSelection(null);
            setSelectAllNotice('');
            handleChange(
              selectedList.filter(opt => !removeIds.has(toId(opt))),
              { action: 'deselect-option', option: { isSelectAll: true } }
            );
            return;
          }

          let pool = filteredOptions;

          // Everything past the first page is still on the server, so go and get it.
          if (isAsync) {
            setSelectAllLoading(true);
            setSelectAllNotice('');
            try {
              const { options: fetched, truncated } = await collectAllOptions({
                loadOptions,
                search: latestSearchRef.current || '',
                filters: getServerFilterParams(),
                pageSize: SELECT_ALL_PAGE_SIZE,
                maxPages: SELECT_ALL_MAX_PAGES,
                getId: toId,
              });
              pool = mergeOptions(filteredOptions, fetched.filter(matchesActiveFilters));
              // Cached rather than merged into the menu: the value needs every
              // option to resolve its label, the menu does not need to draw them.
              cacheOptions(pool);
              if (truncated) {
                setSelectAllNotice(`Selected the first ${pool.length}. Narrow the filter to reach the rest.`);
              }
            } catch (error) {
              console.error('Select all failed', error);
              setSelectAllNotice('Could not load every match. Please try again.');
              return;
            } finally {
              setSelectAllLoading(false);
            }
          }

          const mapById = new Map(selectedList.map(opt => [toId(opt), opt]));
          pool.forEach(opt => {
            const id = toId(opt);
            if (id !== undefined && !mapById.has(id)) mapById.set(id, opt);
          });

          setBulkSelection({ scope: selectAllScope, ids: pool.map(toId).filter(id => id !== undefined) });
          handleChange(Array.from(mapById.values()), { action: 'select-option', option: { isSelectAll: true } });
        };

        const mergedComponents = {
          ...(enableSelectAll && isMulti ? { MultiValue: MultiValueWithLimit } : {}),
          ...customComponents,
          MenuList: MenuListWithSelectAll,
        };
        const computedIsLoading = Boolean(isLoading || asyncLoading);

        const handleMenuOpen = () => {
          if (!isAsync) return;
          if (!latestSearchRef.current && !inputValue && lastRequestSearchRef.current) {
            loadAsyncOptions({ search: '', page: 1, replace: true, filters: getServerFilterParams() });
            return;
          }
          const onlyPreloaded = preloadList.length && internalOptions.length === preloadList.length;
          if (!internalOptions.length || onlyPreloaded) {
            loadAsyncOptions({ search: latestSearchRef.current || '', page: 1, replace: true, filters: getServerFilterParams() });
          }
        };

        const handleMenuClose = () => {
          if (isMulti) return;
          if (debounceRef.current) clearTimeout(debounceRef.current);
          setInputValue('');
          latestSearchRef.current = '';
        };

        const handleInput = (inputValue, actionMeta) => {
          if (onInputChange) onInputChange(inputValue, actionMeta);
          if (!isMulti && (actionMeta?.action === 'menu-close' || actionMeta?.action === 'input-blur' || actionMeta?.action === 'set-value')) {
            setInputValue('');
            return '';
          }
          if (actionMeta?.action === 'input-change') {
            setInputValue(inputValue);
          }
          if (!isAsync || actionMeta?.action !== 'input-change') return inputValue;

          latestSearchRef.current = inputValue;
          setSelectAllNotice('');
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            setAsyncHasMore(true);
            loadAsyncOptions({ search: inputValue, page: 1, replace: true, filters: getServerFilterParams() });
          }, debounceMs);

          return inputValue;
        };

        const handleKeyDown = (event) => {
          if (!creatable || event.key !== 'Enter') return;
          const raw = inputValue.trim();
          if (!raw) return;
          const exists = internalOptions.some(opt => {
            const label = getLabelOf(opt, getOptionLabel);
            const value = getIdOf(opt, getOptionValue);
            return label === raw || String(value) === raw;
          });
          if (!exists) {
            event.preventDefault();
            handleCreateOption(raw);
          }
        };

        const handleMenuScrollToBottom = () => {
          if (!isAsync || asyncLoading || !asyncHasMore) return;
          loadAsyncOptions({ search: latestSearchRef.current || '', page: asyncPage + 1, replace: false, filters: getServerFilterParams() });
        };

        useEffect(() => {
          if (!isAsync || !enableFilter) return;
          const nextParams = getServerFilterParams();
          const nextKey = JSON.stringify(nextParams);
          const prevKey = lastServerFilterRef.current;
          const hasFilterValue = Object.keys(nextParams || {}).length > 0;
          if (!hasFilterValue && (prevKey === null || prevKey === '{}')) return;
          if (nextKey === prevKey) return;
          lastServerFilterRef.current = nextKey;
          setSelectAllNotice('');
          setAsyncHasMore(true);
          loadAsyncOptions({
            search: latestSearchRef.current || inputValue || '',
            page: 1,
            replace: true,
            filters: nextParams,
          });
        }, [filterKey, filterValue, filterValues, isAsync, enableFilter, serverFilterKeys, loadAsyncOptions, getServerFilterParams, inputValue, effectiveMultiFilter]);

        return (
          <div>
            {/* =================== Filter controls =================== */}
            {enableFilter && (
              <div className="">
                <div className='flex flex-wrap items-start gap-3'>
                  {!effectiveMultiFilter && (
                    <>
                      <div className="flex-1 min-w-[220px]">
                        <div className="form-label mb-1">Filter by</div>
                        <Select
                          options={filterKeyOptions}
                          value={filterKeyOptions.find(opt => opt.value === filterKey)}
                          onChange={opt => { setFilterKey(opt.value); setFilterValue(''); }}
                          isClearable={false}
                          className="p-0 w-full input react-select-container"
                          classNames={getGlobalSelectClass(customClassNames)}
                          classNamePrefix="react-select"
                        />
                      </div>

                      {filterKey && (
                        <div className="flex-1 min-w-[220px]">
                          <div className="form-label mb-1">{formatFilterKeyLabel(filterKey)}</div>
                          <Select
                            options={[{ value: '', label: 'All' }, ...(availableValueOptionsByKey?.[filterKey] || [])]}
                            value={{ value: filterValue, label: filterValue || 'All' }}
                            onChange={opt => setFilterValue(opt.value)}
                            isClearable={false}
                            className="w-full p-0 input react-select-container"
                            classNames={getGlobalSelectClass(customClassNames)}
                            classNamePrefix="react-select"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {effectiveMultiFilter && keysToUse.map((key) => (
                    <div key={key} className="flex-1 min-w-[220px]">
                      <div className="form-label mb-1">{formatFilterKeyLabel(key)}</div>
                      <Select
                        options={[{ value: '', label: 'All' }, ...(availableValueOptionsByKey?.[key] || [])]}
                        value={{ value: filterValues?.[key] || '', label: filterValues?.[key] || 'All' }}
                        onChange={opt => {
                          const next = { ...filterValues, [key]: opt.value };
                          setFilterValues(next);
                        }}
                        isClearable={false}
                        className="p-0 w-full input react-select-container"
                        classNames={getGlobalSelectClass(customClassNames)}
                        classNamePrefix="react-select"
                        placeholder={`Filter by ${key}`}
                      />
                    </div>
                  ))}
                </div>
                {enableFilterModeToggle && (
                  <label className="switch w-fit ml-auto my-3">
                    <DefaultTooltip title='Want more precise results? Enable this to filter using Contact Type and State together.' placement="top" className="max-w-full">
                      <label className="items-center gap-1 mb-1 form-label">
                        <KeenIcon icon="information" />
                        <span className="switch-label">Enable Multi Filter</span>
                      </label>
                    </DefaultTooltip>
                    <input
                      type="checkbox"
                      checked={filterModeMulti}
                      onChange={(e) => setFilterModeMulti(e.target.checked)}
                    />
                  </label>
                )}
              </div>
            )}

            {/* =================== Main Select =================== */}
            <SelectComponent
              ref={selectRef}
              isClearable
              isMulti={isMulti}
              options={filteredOptions}
              value={selected}
              onChange={handleChange}
              onCreateOption={creatable ? handleCreateOption : undefined}
              placeholder={placeholder}
              classNames={getGlobalSelectClass(customClassNames)}
              className={`${transparent ? 'transparent-input' : ''} input react-select-container p-0`}
              classNamePrefix="react-select"
              onFocus={onFocus}
              isLoading={computedIsLoading}
              noOptionsMessage={noOptionsMessage}
              isDisabled={disabled}
              components={mergedComponents}
              closeMenuOnSelect={closeMenuOnSelect}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              enableSelectAll={enableSelectAll}         // used by MenuListWithSelectAll
              _selectAll={{                             // passed to SelectAllControl
                allSelected,
                loading: selectAllLoading,
                notice: selectAllNotice,
                onToggle: handleToggleSelectAll,
              }}
              _maxVisibleValues={MAX_VISIBLE_VALUES}    // passed to MultiValueWithLimit
              onOptionSelect={onOptionSelect}
              menuPlacement={menuPlacement}
              menuPosition="fixed"
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              onInputChange={handleInput}
              inputValue={inputValue}
              onKeyDown={handleKeyDown}
              filterOption={isAsync ? () => true : undefined}
            />
          </div>
        );
      }}
    </Field>
  );
};

export { LazyFormikSelect };
